import React, { useCallback, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import contentSlice from '../../reducers/content'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { mapContentPayload } from './util'
import { useEventsOnViewUnload, useGroup, useProducts, useRedirect } from '../../hooks'
import { ALL_AUTHENTICATED } from '../../services/cognito/userGroups'
import NotFound from '../not-found'
import Error from '../error'
import { useSEO } from '../../components/seo-context'
import { ArticleViewLoading } from '../../components/article-view'
import { CONTENT_QUERY_STRING } from '../../consts'
import { playlistNavigationSlice } from '../../reducers'
import ArticleView from './../../components/article-view/article-view'
import { useContentLoad } from './useContentLoad'
import { useStackOneTrackCompletion } from '../../hooks/useStackOneTrackCompletion'

const Content = () => {
  const dispatch = useDispatch()
  const { search } = useLocation()
  const { locationId, '*': staticUrl } = useParams()
  const navigate = useNavigate()
  const { isInGroup } = useGroup()
  const content = useSelector(contentSlice.selectors.selectContent)
  const defaultLanguage = localStorage.getItem('defaultLanguage')

  const [viewedContent] = useEvent([{ eventName: 'contentViewed' }])
  const [contentExited] = useEvent([{ eventName: 'contentExited' }])
  const [getContentExtraInfo] = useEvent([{
    eventName: 'contentGetTenantContent',
    onSuccess: (payload) => {
      dispatch(contentSlice.actions.setExtraInfo(payload))
    }
  }])

  useEffect(() => {
    if (content?.extraInfo && isInGroup(ALL_AUTHENTICATED)) {
      const params = Object.fromEntries(new URLSearchParams(search))
      viewedContent.trigger({
        ...mapContentPayload({ content }),
        ...params,
        reporting: true
      })
    }
  }, [content, isInGroup, search, viewedContent])

  const [contentAutoTranslated, videoCaptionLoaded] = useEvent([
    { eventName: 'contentAutoTranslated' },
    { eventName: 'videoCaptionLoaded' }
  ])

  const isUrlAdjusted = useSelector(contentSlice.selectors.selectIsUrlAdjusted)

  const adjustUrl = useCallback((content) => {
    dispatch(contentSlice.actions.setIsUrlAdjusted(true))
    const toAppendInUrl = content.staticUrl?.length ? content.staticUrl : content.title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()

    // Allow certain query strings to bypass the URL adjustment.
    const allowedQueryString = [
      CONTENT_QUERY_STRING.TRANSLATION,
      CONTENT_QUERY_STRING.PLAYLIST_ID,
      CONTENT_QUERY_STRING.UTM_SOURCE,
      CONTENT_QUERY_STRING.UTM_MEDIUM,
      CONTENT_QUERY_STRING.UTM_CAMPAIGN,
      CONTENT_QUERY_STRING.FROM
    ]
    const urlQueryObject = Array.from(new URLSearchParams(search))
      .filter(([key, value]) => (allowedQueryString.includes(key) && value !== ''))
    const urlQueryString = new URLSearchParams(urlQueryObject).toString()
    const appendQueryString = urlQueryString.length ? `?${urlQueryString}` : ''

    navigate(`/${content.locationId}/${toAppendInUrl}${appendQueryString}`, { replace: true })
  }, [navigate, dispatch, search])

  // If we have a playlistId set in the url this content will appear with the playlist nav/view.
  const setPlaylistContext = useCallback(() => {
    const params = Object.fromEntries(new URLSearchParams(search).entries())
    dispatch(playlistNavigationSlice.actions.setPlaylistId(params?.playlistId))
  }, [dispatch, search])

  const { redirect } = useSEO(!!content)
  useStackOneTrackCompletion()

  const onLoadExtraInfo = useCallback(async () => {
    if (!isUrlAdjusted) {
      dispatch(contentSlice.actions.setIsLoadingExtraInfo(true))
      getContentExtraInfo.trigger({ locationId })
    }
  }, [dispatch, getContentExtraInfo, locationId, isUrlAdjusted])

  const onLoadContent = useCallback(async (content) => {
    onLoadExtraInfo()
    setPlaylistContext()
    adjustUrl(content)

    if (isInGroup(ALL_AUTHENTICATED)) {
      const params = Object.fromEntries(new URLSearchParams(search))

      const language = params.lang
      const locationId = content.locationId
      if (language && language === defaultLanguage && defaultLanguage !== 'en') {
        contentAutoTranslated.trigger({
          locationId,
          language
        })
      }

      // Track dot digital
      window.dmPt && window.dmPt('track', { title: content?.title, pk: content?.pk })

      if (!staticUrl && content.staticUrl) {
        redirect(`Location: ${origin}/${content.locationId}/${content.staticUrl}`)
      } else {
        redirect(null)
      }
    }
  }, [adjustUrl, isInGroup, search, setPlaylistContext, redirect, staticUrl, onLoadExtraInfo, contentAutoTranslated, defaultLanguage])

  const { error, isLoadingContent } = useContentLoad({
    onLoadContent
  })

  useEffect(() => {
    if ((!isInGroup(ALL_AUTHENTICATED) && isUrlAdjusted)) {
      const { pathname, search } = window.location
      localStorage.setItem('after-auth-location', `${pathname}${search}`)
    }
  }, [isInGroup, isUrlAdjusted])

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [])

  // Track JWPlayer video caption usage
  useEffect(() => {
    const handlePlayerEvent = ({ origin, data: eventBody, isTrusted }) => {
      const sameOrigin = window.location.origin === origin && isTrusted
      const { eventName, data } = eventBody

      if (sameOrigin && data && eventName === 'captionLoaded') {
        const selectedCaption = data.track !== 0 ? data.tracks[data.track] : undefined

        if (selectedCaption) {
          const [mediaId] = data.videoId.split('-')

          videoCaptionLoaded.trigger({
            provider: 'jwplayer',
            captionLanguageCode: selectedCaption.language?.toUpperCase() ?? 'EN',
            locationId,
            mediaId
          })
        }
      }
    }
    window.addEventListener('message', handlePlayerEvent)

    return () => {
      window.removeEventListener('message', handlePlayerEvent)
    }
  }, [videoCaptionLoaded, locationId])

  useEventsOnViewUnload((duration) => {
    contentExited.trigger({ duration, locationId })
  }, [contentExited, locationId])

  useEffect(() => () => {
    dispatch(contentSlice.actions.reset(false))
  }, [dispatch, locationId])

  const isBusy = isLoadingContent

  if (error) {
    return error.status === 404 ? <NotFound /> : <Error />
  }

  if (isBusy || !isUrlAdjusted || !content) return <ArticleViewLoading />

  return <ArticleView />
}

const Page = () => {
  const { hasContentHubAccess } = useProducts()
  const { standaloneRedirect } = useRedirect()

  useEffect(() => {
    if (!hasContentHubAccess) standaloneRedirect()
  }, [hasContentHubAccess, standaloneRedirect])

  if (!hasContentHubAccess) return null

  return <Content />
}

export default React.memo(Page)
